import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import PageLayout from '../components/layout/PageLayout'
import FloatingFabs from '@astro/floating-fabs'
import { useToast } from '../context/ToastContext'
import { useRazorpayCheckout } from '../hooks/useRazorpayCheckout'
import { releaseUnpaidBooking } from '../utils/bookingCheckout'
import { decorateAstrologerDetail, unwrapAstrologerRecord, toBookingConsultationType } from '../components/sections/astrologerDetails/astrologerDetailsData'
import { toIsoDate, formatDateLabel, formatTimeLabel } from '../utils/date'
import { resolveRazorpayKey } from '../utils/payments'
import BookingHeroSection from '../components/sections/booking/BookingHeroSection'
import BookingStepper from '../components/sections/booking/BookingStepper'
import ScheduleCalendarSection from '../components/sections/booking/ScheduleCalendarSection'
import ExpertProfileSummaryCard from '../components/sections/booking/ExpertProfileSummaryCard'
import PremiumMaharajSection from '../components/sections/booking/PremiumMaharajSection'
import BookingTrustSection from '../components/sections/booking/BookingTrustSection'
import BirthDetailsForm, { isBirthDetailsValid } from '../components/sections/booking/BirthDetailsForm'
import BookingPageSkeleton from '../components/sections/booking/BookingPageSkeleton'

export default function BookingPage() {
  const { id } = useParams()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const location = useLocation()
  const { status: authStatus, user, login } = useAuth()
  const { open: openRazorpay } = useRazorpayCheckout()

  // Pre-filled when the astrologer detail page's quick-book sidebar already
  // had the seeker pick a date/slot before handing off here.
  const [selectedDate, setSelectedDate] = useState(() => location.state?.selectedDate ?? null)
  const [selectedSlot, setSelectedSlot] = useState(() => location.state?.selectedSlot ?? null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [birthDetails, setBirthDetails] = useState({ birthDate: '', birthTime: '', place: '', placeLocation: null, notes: '' })

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['astrologer', id],
    queryFn: () => api.astrologers.get(id),
    enabled: Boolean(id),
  })

  // Live-configured gateway key wins over any local VITE_RAZORPAY_KEY —
  // this endpoint is what actually knows which gateway is active in prod.
  const { data: gatewaysData } = useQuery({
    queryKey: ['payment-gateways'],
    queryFn: () => api.payments.gateways(),
    staleTime: 10 * 60 * 1000,
  })
  const razorpayKey = resolveRazorpayKey(gatewaysData)

  const record = data ? unwrapAstrologerRecord(data) : null
  const astro = record ? decorateAstrologerDetail(record) : null
  const consultationType = astro?.consultationMode?.toUpperCase() ?? 'ASTROLOGY CONSULTATION'
  const selectedTime = selectedSlot?.start_time ?? selectedSlot?.time ?? null

  const handleProceed = async () => {
    if (!astro || !selectedDate || !selectedSlot || isSubmitting) return
    if (!isBirthDetailsValid(birthDetails)) {
      showToast('Please fill in your date, time and place of birth to continue.')
      return
    }

    if (authStatus !== 'authenticated') {
      showToast('Please sign in to continue your booking.')
      // Central login lives on the website app; `login()` defaults returnTo to
      // this page's absolute URL so the seeker lands back here afterwards.
      login()
      return
    }

    setIsSubmitting(true)
    const isoDate = toIsoDate(selectedDate)
    const dateLabel = formatDateLabel(selectedDate)
    const timeLabel = formatTimeLabel(selectedTime)

    let bookingId = null
    try {
      // Backend requires a booking to exist before an order can be created
      // for it, so this reserves the slot first, then pays.
      const bookingResponse = await api.bookings.create({
        name: user?.name ?? '',
        phone: user?.mobile ?? '',
        email: user?.email ?? '',
        consultation_type: toBookingConsultationType(astro.consultationMode),
        astrologer_id: astro.id,
        date: isoDate,
        slot_id: selectedSlot.id,
        payment_method: 'razorpay',
        rate: astro.price,
        birth_date: birthDetails.birthDate,
        birth_time: birthDetails.birthTime,
        place: birthDetails.place,
        notes: birthDetails.notes,
      })
      // Envelope key is inconsistent across this backend's endpoints (the
      // Razorpay verify response nests under `booking`, others under `data`)
      // — try both before falling back to the raw response.
      const booking = bookingResponse?.data ?? bookingResponse?.booking ?? bookingResponse
      bookingId = booking?.id
      if (!bookingId) {
        showToast('Booking was reserved but its id was missing from the response — please contact support.')
        setIsSubmitting(false)
        return
      }
    } catch (err) {
      showToast(err?.error?.message || err?.message || 'Could not reserve this slot. Please try again.')
      setIsSubmitting(false)
      return
    }

    try {
      // Backend converts to paise itself (confirmed live — sending
      // pre-converted paise here doubled the charge to 100x the price).
      const amount = Math.max(1, Math.round(astro.price))
      const orderResponse = await api.bookings.createRazorpayOrder({
        amount,
        currency: 'INR',
        booking_id: bookingId,
      })
      // Same envelope convention as every other endpoint here — the order
      // payload rides under `data`, not the top level.
      const order = orderResponse?.data ?? orderResponse

      const payment = await openRazorpay({
        order,
        keyId: razorpayKey,
        name: 'Astrologer Raju Maharaj Ji',
        description: `${consultationType} · ${dateLabel} at ${timeLabel}`,
      })

      // Verify the signature server-side — this is what marks the booking paid/confirmed.
      await api.bookings.verifyRazorpayPayment({
        booking_id: bookingId,
        razorpay_order_id: payment.razorpay_order_id,
        razorpay_payment_id: payment.razorpay_payment_id,
        razorpay_signature: payment.razorpay_signature,
      })

      showToast(`Booking confirmed · ${astro.priceLabel} · ${dateLabel} at ${timeLabel}`)
      navigate(`/bookings/${bookingId}`)
    } catch (err) {
      // No verified payment, so nothing was booked: release the reservation
      // rather than leave a phantom booking in the seeker's list holding the
      // slot (see releaseUnpaidBooking).
      if (err?.code === 'no_gateway_key') {
        await releaseUnpaidBooking(bookingId, 'Payment gateway unavailable')
        showToast('Payment gateway isn’t configured yet — nothing was booked or charged. Please contact our team.')
      } else if (err?.code === 'cancelled') {
        await releaseUnpaidBooking(bookingId, 'Payment cancelled by customer')
        showToast('Payment cancelled — nothing was booked, and the slot has been released.')
      } else {
        await releaseUnpaidBooking(bookingId, 'Payment failed')
        showToast(err?.error?.message || err?.message || 'Payment could not be completed, so nothing was booked. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PageLayout className="bg-[#403c3d]/5 text-[#151b21] antialiased min-h-screen flex flex-col">
      <main className="grow">
        {isPending ? (
          <BookingPageSkeleton />
        ) : isError || !astro ? (
          <div className="pt-40 pb-24 text-center px-6">
            <div className="text-5xl mb-4 opacity-40">🛰️</div>
            <p className="font-cinzel text-lg font-bold mb-1 text-[#1C140A]">Couldn&rsquo;t load this astrologer</p>
            <p className="text-sm mb-5 text-[#8B7355]">The profile may no longer be available, or something went wrong.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(200,146,42,.35)', color: '#C8922A', background: 'rgba(200,146,42,.06)' }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="flex flex-col min-h-screen bg-background pb-20 relative">
            <BookingHeroSection astro={astro} />

            <section className="container px-6 mx-auto mt-10">
              <BookingStepper isComplete={Boolean(selectedDate && selectedTime && isBirthDetailsValid(birthDetails))} />
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-7 space-y-8">
                  <ScheduleCalendarSection
                    astrologerId={astro.id}
                    selectedDate={selectedDate}
                    selectedSlot={selectedSlot}
                    onSelectDate={(date) => { setSelectedDate(date); setSelectedSlot(null) }}
                    onSelectSlot={setSelectedSlot}
                  />
                  <BirthDetailsForm value={birthDetails} onChange={setBirthDetails} />
                </div>
                <aside className="lg:col-span-5 lg:sticky lg:top-28">
                  <ExpertProfileSummaryCard
                    astro={astro}
                    consultationType={consultationType}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    birthDetailsValid={isBirthDetailsValid(birthDetails)}
                    onProceed={handleProceed}
                    isSubmitting={isSubmitting}
                  />
                </aside>
              </div>
            </section>

            <PremiumMaharajSection excludeId={astro.id} />

            <BookingTrustSection />
          </div>
        )}
      </main>
      <FloatingFabs />
    </PageLayout>
  )
}
